/**
 * `packages/shared/src/deeplink.ts` — the payload of a direct link, both ways.
 *
 * Task 15. `spec.md` «Бот»: «Кнопка меню — ссылка `t.me/<бот>/<приложение>?startapp=home`» and,
 * under every track the bot sends, «кнопка „Открыть в плеере“ с глубокой ссылкой». The bot
 * writes the payload, the app reads it back out of the launch, and task 16's digest writes it
 * again beside every new track. One module for both directions is what keeps the three from
 * drifting into two spellings.
 *
 * ## `t<track id>`, and not the `short_id` column
 *
 * `0002_catalogue.sql` left `tracks.short_id` «for the startapp deep link», to be minted on
 * demand. It is not used: the track id already is a stable name — the indexer never replaces a
 * row (`apps/indexer/src/upsert.ts`, rule 1), so a link written today names the same track
 * next year — and it is already public, in every address the app calls. A second id would buy
 * a write on the first share of every track and a collision to retry, for nothing a listener
 * can see. The column stays, unwritten.
 *
 * ## `v<record id>` for a clip (task 16)
 *
 * The weekly digest lists the week's clips beside its tracks (`spec.md` «Бот»: «видео входят в
 * список»), and a line of it opens what it names. A clip is not a track — `GET /api/tracks/:id`
 * answers 404 for one, and the app plays it in «Видео», not in the queue — so it has a letter of
 * its own rather than a second meaning of `t`. The number is the same `tracks.id`, stable for the
 * same reason.
 *
 * ## Anything unknown is the home screen
 *
 * The checklist of task 15: «неизвестный параметр — главный экран без ошибки». A
 * link is typed by people, forwarded, cut short by a chat client; none of that may reach the
 * listener as an error. So {@link parseStartParam} never throws and has exactly two answers.
 * Only the canonical spelling is a track: `t042` or `t 42` would be a second name for one
 * track, and a second name is how a link that «almost works» gets shared around.
 */
/** The payload of a bare launch: the menu button and `/start`. */
export const START_PARAM_HOME = 'home';
const HOME = Object.freeze({ kind: 'home' });
/**
 * `t` (a track) or `v` (a clip) and a record id in plain decimal digits: no sign, no leading
 * zero, no exponent.
 */
const RECORD_PARAM_RE = /^([tv])([1-9]\d{0,15})$/u;
/** The one check both writers share: a link to nothing would only fail when somebody taps it. */
function recordId(writer, id) {
    if (!Number.isSafeInteger(id) || id <= 0) {
        throw new RangeError(`${writer}: a record id is a positive integer, got ${String(id)}`);
    }
    return String(id);
}
/**
 * The payload that opens track `trackId`.
 *
 * Throws on anything that is not a track id, because a link to nothing would be sent to a
 * person and only fail when they tap it.
 */
export function trackStartParam(trackId) {
    return `t${recordId('trackStartParam', trackId)}`;
}
/** The payload that opens clip `videoId` in «Видео». Throws like {@link trackStartParam}. */
export function videoStartParam(videoId) {
    return `v${recordId('videoStartParam', videoId)}`;
}
/** What `raw` — `start_param` of a launch, as the server read it — asks to show. */
export function parseStartParam(raw) {
    if (typeof raw !== 'string')
        return HOME;
    const match = RECORD_PARAM_RE.exec(raw);
    const letter = match?.[1];
    const id = Number(match?.[2]);
    if (letter === undefined || !Number.isSafeInteger(id))
        return HOME;
    return letter === 't' ? { kind: 'track', trackId: id } : { kind: 'video', videoId: id };
}
//# sourceMappingURL=deeplink.js.map